import type { TileFace } from '../types/tile.ts';
import { rotationCandidates } from './rotation.ts';

export type SlotIndex = number;

export type Placement = {
  resolvedNumber: number;
  targetIdx: SlotIndex;
};

/**
 * placement 계산은 face 와 resolvedNumber만 사용 — Tile/MaskedTile 모두 호환.
 */
type PlaceableTileLike = {
  face: TileFace;
  resolvedNumber: number | null;
};

type PlaceableLaneLike = {
  slots: ReadonlyArray<{ tile: PlaceableTileLike }>;
};

/**
 * findAllPlacements 호출 시 number는 항상 known이어야 함 (자기 holding은 number 노출됨).
 */
type HoldingTileLike = {
  number: number;
  resolvedNumber: number | null;
};

/**
 * 자기 줄에 N을 놓을 수 있는 비공개 자리 인덱스 목록.
 *
 * 조건:
 *  - 그 자리는 face='DOWN' (비공개)
 *  - 그 자리 좌측의 모든 *공개* 타일의 max < N
 *  - 그 자리 우측의 모든 *공개* 타일의 min > N
 */
export function findPlaceableSlots(lane: PlaceableLaneLike, n: number): SlotIndex[] {
  const result: SlotIndex[] = [];
  const rightMin = computeRightOpenMin(lane);
  let leftMax = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < lane.slots.length; i++) {
    const slot = lane.slots[i];
    if (!slot) continue;
    if (slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
      // 공개 자리는 후보 아님. 좌측 max 업데이트.
      if (slot.tile.resolvedNumber > leftMax) leftMax = slot.tile.resolvedNumber;
      continue;
    }
    // 비공개 자리
    const right = rightMin[i] ?? Number.POSITIVE_INFINITY;
    if (leftMax < n && n < right) {
      result.push(i);
    }
  }
  return result;
}

function computeRightOpenMin(lane: PlaceableLaneLike): number[] {
  const out = new Array<number>(lane.slots.length).fill(Number.POSITIVE_INFINITY);
  let rightMin = Number.POSITIVE_INFINITY;
  for (let i = lane.slots.length - 1; i >= 0; i--) {
    const slot = lane.slots[i];
    if (slot && slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
      if (slot.tile.resolvedNumber < rightMin) rightMin = slot.tile.resolvedNumber;
    }
    out[i] = rightMin;
  }
  return out;
}

/**
 * 특정 자리에 N을 놓을 수 있는지 검증.
 */
export function isPlaceable(lane: PlaceableLaneLike, n: number, targetIdx: SlotIndex): boolean {
  const slot = lane.slots[targetIdx];
  if (!slot || slot.tile.face !== 'DOWN') return false;
  const placeable = findPlaceableSlots(lane, n);
  return placeable.includes(targetIdx);
}

/**
 * 자기 줄에 가져온 타일을 둘 수 있는 모든 (resolvedNumber, targetIdx) 조합.
 * 회전 가능 타일이면 두 후보 모두 검사.
 *
 * 풀에서 가져온 타일이 이미 결정된 resolvedNumber를 가진 경우, 그 값만 사용.
 */
export function findAllPlacements(lane: PlaceableLaneLike, tile: HoldingTileLike): Placement[] {
  const candidates =
    tile.resolvedNumber !== null ? [tile.resolvedNumber] : rotationCandidates(tile.number);

  const out: Placement[] = [];
  for (const n of candidates) {
    for (const idx of findPlaceableSlots(lane, n)) {
      out.push({ resolvedNumber: n, targetIdx: idx });
    }
  }
  return out;
}
